import { isMotionLinkSession, normalizeMotionLinkCaptureSession } from './motionlink.js';
import { LIMONADE_MOTIONLINK_SCHEMA, } from './types.js';
const MODULE_ID = 'motionlink-processor';
const LOW_CONFIDENCE_WARNING = 'Low tracking confidence';
const DRIFT_WARNING = 'Sensor drift detected';
const MISSING_TIMESTAMP_WARNING = 'Missing timestamp samples';
const UNSYNCED_WARNING = 'Motion data not synced to video';
const CALIBRATION_WARNING = 'Calibration missing';
const OPTICAL_FLOW_DISAGREES_WARNING = 'Optical flow disagrees with IMU motion';
const INSUFFICIENT_VISUAL_WARNING = 'Insufficient visual motion data';
const UNKNOWN_MOUNT_WARNING = 'Phone mount orientation unknown';
const LARGE_OFFSET_WARNING = 'Large offset may reduce accuracy';
const RECALIBRATION_WARNING = 'Recalibration recommended';
const ZERO_VECTOR = { x: 0, y: 0, z: 0 };
const IDENTITY_QUATERNION = { x: 0, y: 0, z: 0, w: 1 };
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 6) => Number(value.toFixed(digits));
const lerp = (a, b, t) => a + (b - a) * t;
const magnitude = (vector) => vector ? Math.hypot(vector.x, vector.y, vector.z) : 0;
const radiansToDegrees = (value) => value * (180 / Math.PI);
const normalizeAngleDelta = (radians) => {
    let value = radians;
    while (value > Math.PI)
        value -= Math.PI * 2;
    while (value < -Math.PI)
        value += Math.PI * 2;
    return value;
};
const panTypeFromYaw = (yawDegreesOrRadians) => yawDegreesOrRadians >= 0 ? 'pan_left' : 'pan_right';
const panDirectionFromYaw = (yawDegreesOrRadians) => yawDegreesOrRadians >= 0 ? 'left' : 'right';
function warning(code, message, frameNumber) {
    return {
        code,
        severity: 'warning',
        message,
        module_id: MODULE_ID,
        ...(frameNumber === undefined ? {} : { frame_number: frameNumber }),
    };
}
function addUniqueWarning(warnings, next) {
    if (!warnings.some((item) => item.message === next.message && item.frame_number === next.frame_number)) {
        warnings.push(next);
    }
}
function isVector(value) {
    return value !== null && value !== undefined;
}
function isQuaternionValue(value) {
    return value !== null && value !== undefined;
}
function normalizeQuaternion(value) {
    const length = Math.hypot(value.x, value.y, value.z, value.w);
    if (length <= 0.000001)
        return { ...IDENTITY_QUATERNION };
    return {
        x: value.x / length,
        y: value.y / length,
        z: value.z / length,
        w: value.w / length,
    };
}
function quaternionMultiply(a, b) {
    return normalizeQuaternion({
        w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
        x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
        y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
        z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    });
}
function quaternionInverse(value) {
    const normalized = normalizeQuaternion(value);
    return {
        x: -normalized.x,
        y: -normalized.y,
        z: -normalized.z,
        w: normalized.w,
    };
}
function axisAngleQuaternion(axis, radians) {
    const half = radians / 2;
    const sin = Math.sin(half);
    return normalizeQuaternion({
        x: axis === 'x' ? sin : 0,
        y: axis === 'y' ? sin : 0,
        z: axis === 'z' ? sin : 0,
        w: Math.cos(half),
    });
}
function orientationToRotationOffset(orientation) {
    switch (orientation) {
        case 'side_left':
            return axisAngleQuaternion('z', Math.PI / 2);
        case 'side_right':
            return axisAngleQuaternion('z', -Math.PI / 2);
        case 'upside_down':
            return axisAngleQuaternion('z', Math.PI);
        case 'top':
        case 'custom':
        default:
            return { ...IDENTITY_QUATERNION };
    }
}
function lerpVector(a, b, t) {
    if (!a && !b)
        return null;
    const left = a ?? b ?? ZERO_VECTOR;
    const right = b ?? a ?? ZERO_VECTOR;
    return {
        x: lerp(left.x, right.x, t),
        y: lerp(left.y, right.y, t),
        z: lerp(left.z, right.z, t),
    };
}
function lerpQuaternion(a, b, t) {
    if (!a && !b)
        return null;
    const left = a ?? b ?? IDENTITY_QUATERNION;
    const right = b ?? a ?? IDENTITY_QUATERNION;
    return normalizeQuaternion({
        x: lerp(left.x, right.x, t),
        y: lerp(left.y, right.y, t),
        z: lerp(left.z, right.z, t),
        w: lerp(left.w, right.w, t),
    });
}
function lerpEuler(a, b, t) {
    if (!a && !b)
        return null;
    const left = a ?? b ?? { pitch: 0, yaw: 0, roll: 0, order: 'XYZ' };
    const right = b ?? a ?? { pitch: 0, yaw: 0, roll: 0, order: 'XYZ' };
    return {
        pitch: lerp(left.pitch, right.pitch, t),
        yaw: lerp(left.yaw, right.yaw, t),
        roll: lerp(left.roll, right.roll, t),
        order: left.order ?? right.order ?? 'XYZ',
    };
}
export function quaternionToEuler(rotation) {
    const q = normalizeQuaternion(rotation);
    const sinrCosp = 2 * (q.w * q.x + q.y * q.z);
    const cosrCosp = 1 - 2 * (q.x * q.x + q.y * q.y);
    const roll = Math.atan2(sinrCosp, cosrCosp);
    const sinp = 2 * (q.w * q.y - q.z * q.x);
    const pitch = Math.abs(sinp) >= 1 ? Math.sign(sinp) * (Math.PI / 2) : Math.asin(sinp);
    const sinyCosp = 2 * (q.w * q.z + q.x * q.y);
    const cosyCosp = 1 - 2 * (q.y * q.y + q.z * q.z);
    const yaw = Math.atan2(sinyCosp, cosyCosp);
    return {
        pitch: round(pitch),
        yaw: round(yaw),
        roll: round(roll),
        order: 'XYZ',
    };
}
export function createCameraRigCalibration(options) {
    const presetRotation = orientationToRotationOffset(options.phone_mount_orientation);
    const rotationOffset = normalizeQuaternion(options.rotation_offset ?? presetRotation);
    const positionOffset = options.position_offset ?? ZERO_VECTOR;
    const lensOffset = options.lens_offset ?? ZERO_VECTOR;
    const totalOffsetMeters = magnitude({
        x: positionOffset.x + lensOffset.x,
        y: positionOffset.y + lensOffset.y,
        z: positionOffset.z + lensOffset.z,
    });
    const offsetPenalty = clamp01(totalOffsetMeters / 0.5) * 0.25;
    const customPenalty = options.phone_mount_orientation === 'custom' && !options.rotation_offset ? 0.15 : 0;
    const calibrationQuality = clamp01(1 - offsetPenalty - customPenalty);
    return {
        calibrated_at: options.calibrated_at ?? new Date().toISOString(),
        camera_name: options.camera_name ?? null,
        lens_name: options.lens_name ?? null,
        phone_mount_orientation: options.phone_mount_orientation,
        phone_mount_position: options.phone_mount_orientation === 'top' ? 'top' : 'unknown',
        phone_to_camera_rotation: rotationOffset,
        phone_to_camera_translation_m: {
            x: positionOffset.x + lensOffset.x,
            y: positionOffset.y + lensOffset.y,
            z: positionOffset.z + lensOffset.z,
        },
        baseline_orientation: normalizeQuaternion(options.baseline_orientation),
        rotation_offset: rotationOffset,
        position_offset: positionOffset,
        lens_offset: lensOffset,
        scale_factor: options.scale_factor ?? 1,
        stabilization_strength: clamp01(options.stabilization_strength ?? 0.35),
        calibration_quality: round(calibrationQuality, 4),
        sensor_time_offset_ms: options.sensor_time_offset_ms ?? 0,
        notes: options.notes,
    };
}
function averageVector(values) {
    const vectors = values.filter(isVector);
    if (vectors.length === 0)
        return null;
    return {
        x: vectors.reduce((sum, value) => sum + value.x, 0) / vectors.length,
        y: vectors.reduce((sum, value) => sum + value.y, 0) / vectors.length,
        z: vectors.reduce((sum, value) => sum + value.z, 0) / vectors.length,
    };
}
function averageQuaternion(values) {
    const quaternions = values.filter(isQuaternionValue);
    if (quaternions.length === 0)
        return null;
    return normalizeQuaternion({
        x: quaternions.reduce((sum, value) => sum + value.x, 0) / quaternions.length,
        y: quaternions.reduce((sum, value) => sum + value.y, 0) / quaternions.length,
        z: quaternions.reduce((sum, value) => sum + value.z, 0) / quaternions.length,
        w: quaternions.reduce((sum, value) => sum + value.w, 0) / quaternions.length,
    });
}
function averageEuler(values) {
    const rotations = values.filter((value) => value !== null);
    if (rotations.length === 0)
        return null;
    return {
        pitch: rotations.reduce((sum, value) => sum + value.pitch, 0) / rotations.length,
        yaw: rotations.reduce((sum, value) => sum + value.yaw, 0) / rotations.length,
        roll: rotations.reduce((sum, value) => sum + value.roll, 0) / rotations.length,
        order: rotations[0]?.order ?? 'XYZ',
    };
}
function mergeFrameSample(frame) {
    const camera = frame.camera_motion_estimate;
    const arkit = frame.arkit_pose;
    const imu = frame.imu;
    const rotationQuaternion = camera?.rotation_quaternion ?? arkit?.rotation_quaternion ?? imu?.rotation_quaternion ?? frame.rotation_quaternion;
    const rotationEuler = camera?.rotation_euler ??
        arkit?.rotation_euler ??
        imu?.rotation_euler ??
        frame.rotation_euler ??
        (rotationQuaternion ? quaternionToEuler(rotationQuaternion) : null);
    const confidenceValues = [
        frame.tracking_confidence,
        camera?.tracking_confidence,
        camera?.confidence,
        arkit?.tracking_confidence,
        imu?.tracking_confidence,
    ].filter((value) => typeof value === 'number' && Number.isFinite(value));
    const trackingConfidence = confidenceValues.length
        ? confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length
        : 0.5;
    return {
        sample_type: 'camera_motion_estimate',
        timestamp_ms: frame.timestamp_ms,
        frame_number: frame.frame_number,
        rotation_quaternion: rotationQuaternion ?? null,
        rotation_euler: rotationEuler ?? null,
        acceleration: camera?.acceleration ?? imu?.acceleration ?? frame.acceleration ?? null,
        angular_velocity: camera?.angular_velocity ?? imu?.angular_velocity ?? frame.angular_velocity ?? null,
        position: camera?.position ?? arkit?.position ?? frame.position ?? null,
        velocity: camera?.velocity ?? arkit?.velocity ?? frame.velocity ?? null,
        tracking_confidence: clamp01(trackingConfidence),
        motion_type: camera?.motion_type ?? frame.motion_type ?? 'unknown',
        confidence: clamp01(camera?.confidence ?? trackingConfidence),
        source: camera?.source ?? (arkit && imu ? 'fused' : arkit ? 'arkit' : imu ? 'imu' : 'unknown'),
    };
}
function subtractVector(a, b) {
    return {
        x: a.x - b.x,
        y: a.y - b.y,
        z: a.z - b.z,
    };
}
function scaleVector(vector, scale) {
    return {
        x: vector.x * scale,
        y: vector.y * scale,
        z: vector.z * scale,
    };
}
function applyRigCalibration(estimate, calibration) {
    const rotationOffset = calibration.rotation_offset ??
        calibration.phone_to_camera_rotation ??
        orientationToRotationOffset(calibration.phone_mount_orientation);
    const baseline = calibration.baseline_orientation ?? IDENTITY_QUATERNION;
    const correctedQuaternion = estimate.rotation_quaternion
        ? quaternionMultiply(rotationOffset, quaternionMultiply(quaternionInverse(baseline), estimate.rotation_quaternion))
        : null;
    const positionOffset = calibration.position_offset ?? calibration.phone_to_camera_translation_m ?? ZERO_VECTOR;
    const lensOffset = calibration.lens_offset ?? ZERO_VECTOR;
    const totalOffset = {
        x: positionOffset.x + lensOffset.x,
        y: positionOffset.y + lensOffset.y,
        z: positionOffset.z + lensOffset.z,
    };
    const scaleFactor = Number.isFinite(calibration.scale_factor) ? calibration.scale_factor : 1;
    return {
        ...estimate,
        rotation_quaternion: correctedQuaternion,
        rotation_euler: correctedQuaternion ? quaternionToEuler(correctedQuaternion) : estimate.rotation_euler,
        position: estimate.position ? scaleVector(subtractVector(estimate.position, totalOffset), scaleFactor) : estimate.position,
        velocity: estimate.velocity ? scaleVector(estimate.velocity, scaleFactor) : estimate.velocity,
    };
}
function interpolateEstimate(samples, timestampMs, frameNumber, warnings, syncToleranceMs) {
    let before = samples[0];
    let after = samples[samples.length - 1];
    for (let index = 0; index < samples.length; index++) {
        const sample = samples[index];
        if (!sample)
            continue;
        if (sample.timestamp_ms <= timestampMs)
            before = sample;
        if (sample.timestamp_ms >= timestampMs) {
            after = sample;
            break;
        }
    }
    const left = before ?? after;
    const right = after ?? before;
    if (!left || !right) {
        addUniqueWarning(warnings, warning('motionlink_missing_timestamp_samples', MISSING_TIMESTAMP_WARNING, frameNumber));
        return {
            sample_type: 'camera_motion_estimate',
            timestamp_ms: timestampMs,
            frame_number: frameNumber,
            rotation_quaternion: null,
            rotation_euler: null,
            acceleration: null,
            angular_velocity: null,
            position: null,
            velocity: null,
            tracking_confidence: 0,
            motion_type: 'unknown',
            confidence: 0,
            source: 'unknown',
        };
    }
    const closestGap = Math.min(Math.abs(timestampMs - left.timestamp_ms), Math.abs(timestampMs - right.timestamp_ms));
    if (closestGap > syncToleranceMs) {
        addUniqueWarning(warnings, warning('motionlink_not_synced_to_video', UNSYNCED_WARNING, frameNumber));
    }
    const span = right.timestamp_ms - left.timestamp_ms;
    const t = span === 0 ? 0 : clamp01((timestampMs - left.timestamp_ms) / span);
    const rotationQuaternion = lerpQuaternion(left.rotation_quaternion, right.rotation_quaternion, t);
    const rotationEuler = lerpEuler(left.rotation_euler, right.rotation_euler, t) ??
        (rotationQuaternion ? quaternionToEuler(rotationQuaternion) : null);
    const trackingConfidence = clamp01(lerp(left.tracking_confidence, right.tracking_confidence, t));
    const confidence = clamp01(lerp(left.confidence, right.confidence, t));
    return {
        sample_type: 'camera_motion_estimate',
        timestamp_ms: timestampMs,
        frame_number: frameNumber,
        rotation_quaternion: rotationQuaternion,
        rotation_euler: rotationEuler,
        acceleration: lerpVector(left.acceleration, right.acceleration, t),
        angular_velocity: lerpVector(left.angular_velocity, right.angular_velocity, t),
        position: lerpVector(left.position, right.position, t),
        velocity: lerpVector(left.velocity, right.velocity, t),
        tracking_confidence: trackingConfidence,
        motion_type: 'unknown',
        confidence,
        source: left.source === right.source ? left.source : 'fused',
    };
}
function smoothEstimates(estimates, windowSize) {
    const halfWindow = Math.max(0, Math.floor(windowSize / 2));
    return estimates.map((estimate, index) => {
        const window = estimates.slice(Math.max(0, index - halfWindow), Math.min(estimates.length, index + halfWindow + 1));
        const rotationQuaternion = averageQuaternion(window.map((item) => item.rotation_quaternion));
        const rotationEuler = averageEuler(window.map((item) => item.rotation_euler)) ??
            (rotationQuaternion ? quaternionToEuler(rotationQuaternion) : null);
        const trackingConfidence = window.reduce((sum, item) => sum + item.tracking_confidence, 0) / window.length;
        const confidence = window.reduce((sum, item) => sum + item.confidence, 0) / window.length;
        return {
            ...estimate,
            rotation_quaternion: rotationQuaternion,
            rotation_euler: rotationEuler,
            acceleration: averageVector(window.map((item) => item.acceleration)),
            angular_velocity: averageVector(window.map((item) => item.angular_velocity)),
            position: averageVector(window.map((item) => item.position)),
            velocity: averageVector(window.map((item) => item.velocity)),
            tracking_confidence: clamp01(trackingConfidence),
            confidence: clamp01(confidence),
        };
    });
}
function classifyMotion(current, previous, angularVolatility) {
    const angularSpeed = magnitude(current.angular_velocity);
    if (angularSpeed > 1.4 || angularVolatility > 0.45)
        return 'shake';
    if (!previous)
        return 'static';
    const currentEuler = current.rotation_euler;
    const previousEuler = previous.rotation_euler;
    const yawDelta = currentEuler && previousEuler ? currentEuler.yaw - previousEuler.yaw : 0;
    const pitchDelta = currentEuler && previousEuler ? currentEuler.pitch - previousEuler.pitch : 0;
    const rollDelta = currentEuler && previousEuler ? currentEuler.roll - previousEuler.roll : 0;
    const positionDelta = current.position && previous.position
        ? {
            x: current.position.x - previous.position.x,
            y: current.position.y - previous.position.y,
            z: current.position.z - previous.position.z,
        }
        : ZERO_VECTOR;
    const largestRotation = Math.max(Math.abs(yawDelta), Math.abs(pitchDelta), Math.abs(rollDelta));
    const positionMagnitude = magnitude(positionDelta);
    if (largestRotation < 0.012 && positionMagnitude < 0.015 && angularSpeed < 0.12)
        return 'static';
    if (Math.abs(rollDelta) >= largestRotation && Math.abs(rollDelta) > 0.015)
        return 'roll';
    if (Math.abs(yawDelta) >= Math.abs(pitchDelta) && Math.abs(yawDelta) > 0.015) {
        return panTypeFromYaw(yawDelta);
    }
    if (Math.abs(pitchDelta) > 0.015)
        return pitchDelta > 0 ? 'tilt_up' : 'tilt_down';
    if (Math.abs(positionDelta.z) > 0.02)
        return positionDelta.z < 0 ? 'push_in' : 'pull_out';
    if (Math.abs(positionDelta.y) > 0.025)
        return positionDelta.y > 0 ? 'crane_up' : 'crane_down';
    if (angularSpeed > 0.45)
        return 'handheld';
    return 'unknown';
}
function detectDrift(samples) {
    if (samples.length < 4)
        return false;
    const first = samples[0];
    const last = samples[samples.length - 1];
    if (!first?.position || !last?.position)
        return false;
    const positionChange = magnitude({
        x: last.position.x - first.position.x,
        y: last.position.y - first.position.y,
        z: last.position.z - first.position.z,
    });
    const averageAngularSpeed = samples.reduce((sum, sample) => sum + magnitude(sample.angular_velocity), 0) / samples.length;
    return positionChange > 0.25 && averageAngularSpeed < 0.08;
}
function angularVolatility(samples, index) {
    const previous = samples[index - 1];
    const current = samples[index];
    const next = samples[index + 1];
    if (!current)
        return 0;
    const previousSpeed = magnitude(previous?.angular_velocity ?? null);
    const currentSpeed = magnitude(current.angular_velocity);
    const nextSpeed = magnitude(next?.angular_velocity ?? null);
    return Math.max(Math.abs(currentSpeed - previousSpeed), Math.abs(nextSpeed - currentSpeed));
}
function visualEstimateFromInput(input) {
    if ('metrics' in input) {
        return {
            timestamp_ms: input.timestamp_ms,
            frame_number: input.frame_number,
            average_motion_x: input.metrics.average_motion_x,
            average_motion_y: input.metrics.average_motion_y,
            motion_magnitude: input.metrics.motion_magnitude,
            dominant_motion_direction: input.metrics.motion_direction,
            possible_camera_movement: input.metrics.possible_camera_movement,
            shake_score: input.metrics.shake_score,
            confidence: input.metrics.confidence,
            vectors: input.metrics.vectors.map((vector) => ({ ...vector })),
        };
    }
    return input;
}
function normalizeMotionForComparison(motion) {
    if (motion === 'handheld')
        return 'shake';
    return motion;
}
function motionDirectionAgreement(sensorMotion, visualMotion) {
    const sensor = normalizeMotionForComparison(sensorMotion);
    const visual = normalizeMotionForComparison(visualMotion);
    if (sensor === visual)
        return 1;
    if (sensor === 'static' && visual === 'static')
        return 1;
    if (sensor === 'unknown' || visual === 'unknown')
        return 0.5;
    if (sensor === 'shake' && visual === 'shake')
        return 0.85;
    if ((sensor === 'pan_left' || sensor === 'pan_right') && (visual === 'pan_left' || visual === 'pan_right'))
        return 0;
    if ((sensor === 'tilt_up' || sensor === 'tilt_down') && (visual === 'tilt_up' || visual === 'tilt_down'))
        return 0;
    if ((sensor === 'push_in' || sensor === 'pull_out') && (visual === 'push_in' || visual === 'pull_out'))
        return 0.05;
    return 0;
}
function motionMagnitudeEstimate(estimate) {
    const angularSpeed = magnitude(estimate.angular_velocity);
    const velocity = magnitude(estimate.velocity);
    const motion = estimate.motion_type === 'static' ? 0 : 0.35;
    return clamp01(angularSpeed / 1.5 + velocity / 0.4 + motion);
}
function findNearestVisualEstimate(visualEstimates, timestampMs) {
    if (!visualEstimates.length)
        return null;
    let nearest = visualEstimates[0];
    let nearestDelta = Math.abs(timestampMs - nearest.timestamp_ms);
    for (const visual of visualEstimates.slice(1)) {
        const delta = Math.abs(timestampMs - visual.timestamp_ms);
        if (delta < nearestDelta) {
            nearest = visual;
            nearestDelta = delta;
        }
    }
    return nearest ? { visual: nearest, deltaMs: nearestDelta } : null;
}
function compareOpticalFlowWithMotionLink(estimates, visualEstimates, warnings, visualSyncToleranceMs) {
    if (!visualEstimates.length)
        return [];
    return estimates.flatMap((estimate, index) => {
        const nearest = findNearestVisualEstimate(visualEstimates, estimate.timestamp_ms);
        if (!nearest || nearest.deltaMs > visualSyncToleranceMs) {
            addUniqueWarning(warnings, warning('motionlink_insufficient_visual_motion_data', INSUFFICIENT_VISUAL_WARNING, estimate.frame_number));
            return [];
        }
        const directionAgreement = motionDirectionAgreement(estimate.motion_type, nearest.visual.possible_camera_movement);
        const sensorMagnitude = motionMagnitudeEstimate(estimate);
        const visualMagnitude = clamp01(nearest.visual.motion_magnitude / 12);
        const magnitudeAgreement = 1 - clamp01(Math.abs(sensorMagnitude - visualMagnitude));
        const confidenceAgreement = 1 - clamp01(Math.abs(estimate.confidence - nearest.visual.confidence));
        const agreementScore = round(directionAgreement * 0.55 +
            magnitudeAgreement * 0.25 +
            confidenceAgreement * 0.2, 4);
        const disagreement = agreementScore < 0.45 &&
            estimate.confidence > 0.45 &&
            nearest.visual.confidence > 0.35 &&
            estimate.motion_type !== 'unknown' &&
            nearest.visual.possible_camera_movement !== 'unknown';
        if (disagreement) {
            addUniqueWarning(warnings, warning('motionlink_optical_flow_disagrees', OPTICAL_FLOW_DISAGREES_WARNING, estimate.frame_number ?? index + 1));
        }
        return [{
                frame_number: estimate.frame_number ?? index + 1,
                timestamp_ms: estimate.timestamp_ms,
                motionlink_motion_type: estimate.motion_type,
                optical_flow_motion_type: nearest.visual.possible_camera_movement,
                timestamp_delta_ms: round(nearest.deltaMs, 3),
                direction_agreement: round(directionAgreement, 4),
                magnitude_agreement: round(magnitudeAgreement, 4),
                confidence_agreement: round(confidenceAgreement, 4),
                agreement_score: agreementScore,
                warning: disagreement ? OPTICAL_FLOW_DISAGREES_WARNING : null,
            }];
    });
}
function dominantMotionType(estimates) {
    if (!estimates.length)
        return 'unknown';
    const priority = [
        'shake', 'orbit_left', 'orbit_right', 'push_in', 'pull_out', 'pan_left', 'pan_right',
        'tilt_up', 'tilt_down', 'roll', 'handheld', 'static', 'unknown',
    ];
    const counts = new Map();
    estimates.forEach((estimate) => counts.set(estimate.motion_type, (counts.get(estimate.motion_type) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || priority.indexOf(a[0]) - priority.indexOf(b[0]))[0]?.[0] ?? 'unknown';
}
function dominantKnownMotionType(estimates) {
    const known = estimates.filter((estimate) => estimate.motion_type !== 'unknown');
    if (!known.length)
        return dominantMotionType(estimates);
    const moving = known.filter((estimate) => estimate.motion_type !== 'static');
    if (!moving.length)
        return 'static';
    return dominantMotionType(moving);
}
function totalEulerDeltaDegrees(estimates, axis) {
    const rotations = estimates.map((estimate) => estimate.rotation_euler).filter((value) => value !== null);
    if (rotations.length < 2)
        return null;
    return round(radiansToDegrees(normalizeAngleDelta((rotations.at(-1)?.[axis] ?? 0) - (rotations[0]?.[axis] ?? 0))), 3);
}
function totalPositionDelta(estimates, axis) {
    const positions = estimates.map((estimate) => estimate.position).filter(isVector);
    if (positions.length < 2)
        return null;
    return round((positions.at(-1)?.[axis] ?? 0) - (positions[0]?.[axis] ?? 0), 4);
}
function averageShakeIntensity(estimates) {
    if (!estimates.length)
        return 0;
    const angularSpeeds = estimates.map((estimate) => magnitude(estimate.angular_velocity));
    return round(clamp01(angularSpeeds.reduce((sum, value) => sum + value, 0) / angularSpeeds.length / 1.5), 4);
}
function averageConfidence(estimates) {
    if (!estimates.length)
        return 0;
    return round(estimates.reduce((sum, estimate) => sum + estimate.confidence, 0) / estimates.length, 4);
}
function segmentLabel(type) {
    return type.replaceAll('_', ' ');
}
function classifySegmentMotion(panDegrees, tiltDegrees, rollDegrees, shakeIntensity) {
    const pan = panDegrees ?? 0;
    const tilt = tiltDegrees ?? 0;
    const rollValue = rollDegrees ?? 0;
    const absPan = Math.abs(pan);
    const absTilt = Math.abs(tilt);
    const absRoll = Math.abs(rollValue);
    if (shakeIntensity > 0.45)
        return 'shake';
    if (absPan < 4 && absTilt < 4 && absRoll < 4 && shakeIntensity < 0.12)
        return 'static';
    if (absRoll >= 12 && absRoll > absPan * 1.2 && absRoll > absTilt * 1.2)
        return 'roll';
    if (absPan >= 8 && absPan >= absTilt * 0.65)
        return panTypeFromYaw(pan);
    if (absTilt >= 8)
        return tilt >= 0 ? 'tilt_up' : 'tilt_down';
    if (shakeIntensity > 0.18)
        return 'handheld';
    return 'unknown';
}
function segmentFromEstimates(estimates) {
    if (!estimates.length)
        return null;
    const first = estimates[0];
    const last = estimates[estimates.length - 1];
    const panDegrees = totalEulerDeltaDegrees(estimates, 'yaw');
    const tiltDegrees = totalEulerDeltaDegrees(estimates, 'pitch');
    const rollDegrees = totalEulerDeltaDegrees(estimates, 'roll');
    const shakeIntensity = averageShakeIntensity(estimates);
    return {
        start_ms: round(first.timestamp_ms, 3),
        end_ms: round(last.timestamp_ms, 3),
        duration_ms: round(Math.max(0, last.timestamp_ms - first.timestamp_ms), 3),
        start_frame: first.frame_number ?? null,
        end_frame: last.frame_number ?? null,
        motion_type: classifySegmentMotion(panDegrees, tiltDegrees, rollDegrees, shakeIntensity),
        confidence: averageConfidence(estimates),
        pan_degrees: panDegrees,
        tilt_degrees: tiltDegrees,
        roll_degrees: rollDegrees,
        shake_intensity: shakeIntensity,
    };
}
function mergeShortSegments(segments, minimumDurationMs) {
    if (segments.length <= 1)
        return segments;
    const merged = [];
    for (const segment of segments) {
        const previous = merged.at(-1);
        if (previous &&
            (segment.duration_ms < minimumDurationMs || previous.motion_type === segment.motion_type)) {
            const combinedEstimate = {
                ...previous,
                end_ms: segment.end_ms,
                duration_ms: round(segment.end_ms - previous.start_ms, 3),
                end_frame: segment.end_frame,
                motion_type: previous.motion_type === segment.motion_type ? previous.motion_type : (previous.duration_ms >= segment.duration_ms ? previous.motion_type : segment.motion_type),
                confidence: round((previous.confidence + segment.confidence) / 2, 4),
                pan_degrees: previous.pan_degrees !== null && segment.pan_degrees !== null ? round(previous.pan_degrees + segment.pan_degrees, 3) : previous.pan_degrees ?? segment.pan_degrees,
                tilt_degrees: previous.tilt_degrees !== null && segment.tilt_degrees !== null ? round(previous.tilt_degrees + segment.tilt_degrees, 3) : previous.tilt_degrees ?? segment.tilt_degrees,
                roll_degrees: previous.roll_degrees !== null && segment.roll_degrees !== null ? round(previous.roll_degrees + segment.roll_degrees, 3) : previous.roll_degrees ?? segment.roll_degrees,
                shake_intensity: round(Math.max(previous.shake_intensity, segment.shake_intensity), 4),
            };
            merged[merged.length - 1] = combinedEstimate;
        }
        else {
            merged.push(segment);
        }
    }
    return merged;
}
function createMotionSegments(estimates) {
    if (!estimates.length)
        return [];
    const windowMs = 1200;
    const minimumDurationMs = 650;
    const rawSegments = [];
    let windowStart = estimates[0].timestamp_ms;
    let current = [];
    for (const estimate of estimates) {
        if (estimate.timestamp_ms - windowStart <= windowMs || current.length < 2) {
            current.push(estimate);
            continue;
        }
        const segment = segmentFromEstimates(current);
        if (segment)
            rawSegments.push(segment);
        current = [estimate];
        windowStart = estimate.timestamp_ms;
    }
    const finalSegment = segmentFromEstimates(current);
    if (finalSegment)
        rawSegments.push(finalSegment);
    return mergeShortSegments(rawSegments, minimumDurationMs)
        .filter((segment) => segment.duration_ms >= minimumDurationMs || segment.motion_type !== 'unknown');
}
function sequencePromptFromSegments(segments) {
    const meaningful = segments
        .filter((segment) => segment.motion_type !== 'unknown')
        .filter((segment) => segment.duration_ms >= 700 || segment.motion_type !== 'static');
    if (meaningful.length < 2)
        return null;
    const panLeftMagnitude = meaningful
        .filter((segment) => segment.motion_type === 'pan_left')
        .reduce((sum, segment) => sum + Math.abs(segment.pan_degrees ?? 0), 0);
    const panRightMagnitude = meaningful
        .filter((segment) => segment.motion_type === 'pan_right')
        .reduce((sum, segment) => sum + Math.abs(segment.pan_degrees ?? 0), 0);
    const strongestPan = Math.max(panLeftMagnitude, panRightMagnitude);
    const weakestPan = Math.min(panLeftMagnitude, panRightMagnitude);
    if (strongestPan >= 45 && strongestPan > Math.max(weakestPan * 3, 20)) {
        return `${strongestPan >= 90 ? 'large ' : ''}pan ${panLeftMagnitude > panRightMagnitude ? 'left' : 'right'}`;
    }
    const collapsed = [];
    const seen = new Set();
    for (const segment of meaningful) {
        if (collapsed.at(-1) !== segment.motion_type && !seen.has(segment.motion_type)) {
            collapsed.push(segment.motion_type);
            seen.add(segment.motion_type);
        }
    }
    const moving = collapsed.filter((type) => type !== 'static');
    const sequence = moving.length >= 2 ? moving : collapsed;
    if (sequence.length < 2)
        return null;
    return sequence.slice(0, 5).map(segmentLabel).join(' then ');
}
function dominantMotionTypeFromSegments(segments) {
    const meaningful = segments.filter((segment) => segment.motion_type !== 'unknown' && segment.motion_type !== 'static');
    if (!meaningful.length)
        return null;
    const scores = new Map();
    for (const segment of meaningful) {
        const motionMagnitude = segment.motion_type === 'pan_left' || segment.motion_type === 'pan_right'
            ? Math.abs(segment.pan_degrees ?? 0)
            : segment.motion_type === 'tilt_up' || segment.motion_type === 'tilt_down'
                ? Math.abs(segment.tilt_degrees ?? 0)
                : segment.motion_type === 'roll'
                    ? Math.abs(segment.roll_degrees ?? 0)
                    : segment.duration_ms / 1000;
        scores.set(segment.motion_type, (scores.get(segment.motion_type) ?? 0) + motionMagnitude);
    }
    return [...scores.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}
function createMotionPromptHelper(estimates, segments = createMotionSegments(estimates)) {
    const initialType = dominantMotionTypeFromSegments(segments) ?? dominantKnownMotionType(estimates);
    const panDegrees = totalEulerDeltaDegrees(estimates, 'yaw');
    const tiltDegrees = totalEulerDeltaDegrees(estimates, 'pitch');
    const rollDegrees = totalEulerDeltaDegrees(estimates, 'roll');
    const zDelta = totalPositionDelta(estimates, 'z');
    const shakeIntensity = averageShakeIntensity(estimates);
    const handheldCount = estimates.filter((estimate) => estimate.motion_type === 'handheld' || estimate.motion_type === 'shake').length;
    const handheldRatio = estimates.length ? handheldCount / estimates.length : 0;
    const hasHandheld = handheldRatio >= 0.08 || shakeIntensity > 0.12;
    const meaningfulMotionCount = estimates.filter((estimate) => estimate.motion_type !== 'unknown' && estimate.motion_type !== 'static').length;
    const panFromDelta = panDegrees !== null && Math.abs(panDegrees) >= 8
        ? panTypeFromYaw(panDegrees)
        : null;
    const tiltFromDelta = tiltDegrees !== null && Math.abs(tiltDegrees) >= 8
        ? tiltDegrees >= 0 ? 'tilt_up' : 'tilt_down'
        : null;
    const pushPullFromDelta = zDelta !== null && Math.abs(zDelta) >= 0.18
        ? zDelta < 0 ? 'push_in' : 'pull_out'
        : null;
    const strongPushPullFromDelta = zDelta !== null && Math.abs(zDelta) >= 0.65
        ? zDelta < 0 ? 'push_in' : 'pull_out'
        : null;
    const fallbackType = initialType === 'unknown' || initialType === 'static'
        ? (pushPullFromDelta ?? panFromDelta ?? tiltFromDelta ?? initialType)
        : (pushPullFromDelta && meaningfulMotionCount < estimates.length * 0.15 ? pushPullFromDelta : initialType);
    const type = strongPushPullFromDelta ?? fallbackType;
    const panText = panDegrees !== null && Math.abs(panDegrees) >= 1.5 && Math.abs(panDegrees) < 12
        ? ` with slight pan ${panDirectionFromYaw(panDegrees)}`
        : '';
    const tiltText = tiltDegrees !== null && Math.abs(tiltDegrees) >= 1.5
        ? ` with ${Math.abs(tiltDegrees) >= 20 ? 'strong' : 'slight'} tilt ${tiltDegrees >= 0 ? 'up' : 'down'}`
        : '';
    const largePanText = panDegrees !== null && Math.abs(panDegrees) >= 12
        ? `${Math.abs(panDegrees) > 45 ? 'large ' : ''}pan ${panDirectionFromYaw(panDegrees)}`
        : null;
    const rollText = rollDegrees !== null && Math.abs(rollDegrees) >= 12
        ? ` with noticeable roll ${rollDegrees >= 0 ? 'right' : 'left'}`
        : '';
    const shakeText = shakeIntensity > 0.45 ? 'strong handheld shake' : 'subtle handheld camera shake';
    let motionPrompt;
    const sequencePrompt = sequencePromptFromSegments(segments);
    if (sequencePrompt && strongPushPullFromDelta && !sequencePrompt.includes(segmentLabel(strongPushPullFromDelta))) {
        motionPrompt = `${segmentLabel(strongPushPullFromDelta)} with ${sequencePrompt}`;
    }
    else if (sequencePrompt) {
        motionPrompt = sequencePrompt;
    }
    else if (type === 'static') {
        motionPrompt = 'locked-off tripod shot';
    }
    else if (type === 'pan_left') {
        motionPrompt = `${hasHandheld ? 'handheld ' : ''}${Math.abs(panDegrees ?? 0) > 45 ? 'large ' : ''}pan left${tiltText}${rollText}`.trim();
    }
    else if (type === 'pan_right') {
        motionPrompt = `${hasHandheld ? 'handheld ' : ''}${Math.abs(panDegrees ?? 0) > 45 ? 'large ' : ''}pan right${tiltText}${rollText}`.trim();
    }
    else if (type === 'tilt_up') {
        motionPrompt = `${hasHandheld ? 'handheld ' : ''}tilt up${panText}`.trim();
    }
    else if (type === 'tilt_down') {
        motionPrompt = `${hasHandheld ? 'handheld ' : ''}tilt down${panText}`.trim();
    }
    else if (type === 'push_in') {
        motionPrompt = `${hasHandheld ? 'handheld ' : ''}push-in${panText}`.trim();
    }
    else if (type === 'pull_out') {
        motionPrompt = `${hasHandheld ? 'handheld ' : ''}pull-out${panText}`.trim();
    }
    else if (type === 'shake') {
        motionPrompt = largePanText
            ? `${shakeText} during ${largePanText}${tiltText}${rollText}`.trim()
            : `${shakeText}${tiltText}${rollText}`.trim();
    }
    else if (type === 'orbit_right') {
        motionPrompt = 'orbit move around subject from left to right';
    }
    else if (type === 'orbit_left') {
        motionPrompt = 'orbit move around subject from right to left';
    }
    else if (type === 'handheld') {
        motionPrompt = `subtle handheld movement${panText}`.trim();
    }
    else {
        motionPrompt = type.replaceAll('_', ' ');
    }
    return {
        motion_prompt: motionPrompt,
        camera_movement: {
            type,
            pan_degrees: panDegrees,
            tilt_degrees: tiltDegrees,
            roll_degrees: rollDegrees,
            shake_intensity: shakeIntensity,
        },
    };
}
export class MotionLinkProcessor {
    load(input) {
        let parsed = input;
        if (typeof input === 'string') {
            try {
                parsed = JSON.parse(input);
            }
            catch {
                throw new Error('Invalid MotionLink JSON: unable to parse document');
            }
        }
        if (!isMotionLinkSession(parsed)) {
            const normalized = normalizeMotionLinkCaptureSession(parsed);
            if (normalized && isMotionLinkSession(normalized)) {
                return normalized;
            }
            throw new Error('Invalid MotionLink JSON: expected schema limonade-motionlink/v1');
        }
        return parsed;
    }
    process(input, options = {}) {
        const session = this.load(input);
        const warnings = [];
        const syncToleranceMs = options.sync_tolerance_ms ?? 80;
        const visualSyncToleranceMs = options.visual_sync_tolerance_ms ?? syncToleranceMs;
        const smoothingWindow = Math.max(1, Math.round(options.smoothing_window ?? 3));
        const lowConfidenceThreshold = options.low_tracking_confidence_threshold ?? 0.45;
        const visualEstimates = (options.optical_flow_results ?? [])
            .map((result) => visualEstimateFromInput(result))
            .filter((result) => Number.isFinite(result.timestamp_ms))
            .sort((a, b) => a.timestamp_ms - b.timestamp_ms);
        const requiresVisualMotion = options.require_visual_motion === true ||
            (options.optical_flow_results !== undefined && visualEstimates.length === 0);
        if (!session.rig_calibration.calibrated_at ||
            !session.rig_calibration.baseline_orientation ||
            !session.rig_calibration.rotation_offset ||
            !session.rig_calibration.phone_to_camera_rotation ||
            !session.rig_calibration.phone_to_camera_translation_m) {
            addUniqueWarning(warnings, warning('motionlink_calibration_missing', CALIBRATION_WARNING));
        }
        if (!session.rig_calibration.phone_mount_orientation) {
            addUniqueWarning(warnings, warning('motionlink_phone_mount_orientation_unknown', UNKNOWN_MOUNT_WARNING));
        }
        const totalRigOffset = magnitude(session.rig_calibration.phone_to_camera_translation_m) +
            magnitude(session.rig_calibration.lens_offset);
        if (totalRigOffset > 0.35) {
            addUniqueWarning(warnings, warning('motionlink_large_offset', LARGE_OFFSET_WARNING));
        }
        if (session.rig_calibration.calibration_quality < 0.55) {
            addUniqueWarning(warnings, warning('motionlink_recalibration_recommended', RECALIBRATION_WARNING));
        }
        const sourceSamples = session.samples
            .map(mergeFrameSample)
            .map((sample) => applyRigCalibration(sample, session.rig_calibration))
            .filter((sample) => Number.isFinite(sample.timestamp_ms))
            .sort((a, b) => a.timestamp_ms - b.timestamp_ms);
        if (sourceSamples.length === 0) {
            addUniqueWarning(warnings, warning('motionlink_missing_timestamp_samples', MISSING_TIMESTAMP_WARNING));
            return {
                session_id: session.session_id,
                schema: LIMONADE_MOTIONLINK_SCHEMA,
                frame_timestamps_ms: [],
                estimates: [],
                motion_segments: [],
                optical_flow_comparisons: [],
                ai_motion_prompt: createMotionPromptHelper([]),
                warnings,
            };
        }
        const frameTimestamps = (options.frame_timestamps_ms?.length
            ? options.frame_timestamps_ms
            : sourceSamples.map((sample) => sample.timestamp_ms))
            .filter((timestamp) => Number.isFinite(timestamp));
        if (frameTimestamps.length === 0) {
            addUniqueWarning(warnings, warning('motionlink_missing_timestamp_samples', MISSING_TIMESTAMP_WARNING));
        }
        const rawEstimates = frameTimestamps.map((timestamp, index) => interpolateEstimate(sourceSamples, timestamp, index + 1, warnings, syncToleranceMs));
        const smoothedEstimates = smoothEstimates(rawEstimates, smoothingWindow);
        const hasDrift = detectDrift(smoothedEstimates);
        if (hasDrift) {
            addUniqueWarning(warnings, warning('motionlink_sensor_drift_detected', DRIFT_WARNING));
        }
        const estimates = smoothedEstimates.map((estimate, index) => {
            const motionType = classifyMotion(estimate, smoothedEstimates[index - 1], angularVolatility(smoothedEstimates, index));
            if (estimate.tracking_confidence < lowConfidenceThreshold) {
                addUniqueWarning(warnings, warning('motionlink_low_tracking_confidence', LOW_CONFIDENCE_WARNING, estimate.frame_number));
            }
            const penalty = (estimate.tracking_confidence < lowConfidenceThreshold ? 0.25 : 0) +
                (hasDrift ? 0.15 : 0) +
                ((session.rig_calibration.calibration_quality ?? 1) < 0.55 ? 0.1 : 0) +
                (warnings.some((item) => item.message === UNSYNCED_WARNING) ? 0.15 : 0);
            return {
                ...estimate,
                rotation_euler: estimate.rotation_euler ??
                    (estimate.rotation_quaternion ? quaternionToEuler(estimate.rotation_quaternion) : null),
                motion_type: motionType,
                confidence: round(clamp01(estimate.confidence - penalty), 4),
                tracking_confidence: round(estimate.tracking_confidence, 4),
                acceleration: estimate.acceleration ? {
                    x: round(estimate.acceleration.x),
                    y: round(estimate.acceleration.y),
                    z: round(estimate.acceleration.z),
                } : null,
                angular_velocity: estimate.angular_velocity ? {
                    x: round(estimate.angular_velocity.x),
                    y: round(estimate.angular_velocity.y),
                    z: round(estimate.angular_velocity.z),
                } : null,
                position: estimate.position ? {
                    x: round(estimate.position.x),
                    y: round(estimate.position.y),
                    z: round(estimate.position.z),
                } : null,
                velocity: estimate.velocity ? {
                    x: round(estimate.velocity.x),
                    y: round(estimate.velocity.y),
                    z: round(estimate.velocity.z),
                } : null,
            };
        });
        if (requiresVisualMotion) {
            addUniqueWarning(warnings, warning('motionlink_insufficient_visual_motion_data', INSUFFICIENT_VISUAL_WARNING));
        }
        const opticalFlowComparisons = compareOpticalFlowWithMotionLink(estimates, visualEstimates, warnings, visualSyncToleranceMs);
        const comparisonPenaltyByFrame = new Map();
        opticalFlowComparisons.forEach((comparison) => {
            if (comparison.warning) {
                comparisonPenaltyByFrame.set(comparison.frame_number, 0.12);
            }
        });
        const finalEstimates = estimates.map((estimate, index) => {
            const frameNumber = estimate.frame_number ?? index + 1;
            const penalty = comparisonPenaltyByFrame.get(frameNumber) ?? 0;
            return penalty
                ? {
                    ...estimate,
                    confidence: round(clamp01(estimate.confidence - penalty), 4),
                    source: estimate.source === 'unknown' ? 'visual' : 'fused',
                }
                : estimate;
        });
        const motionSegments = createMotionSegments(finalEstimates);
        return {
            session_id: session.session_id,
            schema: LIMONADE_MOTIONLINK_SCHEMA,
            frame_timestamps_ms: frameTimestamps,
            estimates: finalEstimates,
            motion_segments: motionSegments,
            optical_flow_comparisons: opticalFlowComparisons,
            ai_motion_prompt: createMotionPromptHelper(finalEstimates, motionSegments),
            warnings,
        };
    }
}
//# sourceMappingURL=MotionLinkProcessor.js.map